import { useState, useRef, useCallback } from "react";
import { api } from "../App";
import { toast } from "sonner";

export default function BugReportWidget() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // Draggable position state
  const [pos, setPos] = useState({ bottom: typeof window !== "undefined" && window.innerWidth < 768 ? 64 : 24, right: 24 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startRight: 24, startBottom: 24 });

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const onMouseDown = useCallback((e) => {
    if (open) return;
    e.preventDefault();
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startRight: pos.right, startBottom: pos.bottom, moved: false };
    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
      setPos({
        right: Math.max(8, Math.min(window.innerWidth - 160, dragRef.current.startRight - dx)),
        bottom: Math.max(8, Math.min(window.innerHeight - 50, dragRef.current.startBottom - dy)),
      });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!dragRef.current.moved) setOpen(o => !o);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [open, pos]);

  // Touch drag for mobile
  const onTouchStart = useCallback((e) => {
    if (open) return;
    const t = e.touches[0];
    dragRef.current = { dragging: true, startX: t.clientX, startY: t.clientY, startRight: pos.right, startBottom: pos.bottom, moved: false };
  }, [open, pos]);
  const onTouchMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const t = e.touches[0];
    const dx = t.clientX - dragRef.current.startX;
    const dy = t.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    setPos({
      right: Math.max(8, Math.min(window.innerWidth - 50, dragRef.current.startRight - dx)),
      bottom: Math.max(70, Math.min(window.innerHeight - 50, dragRef.current.startBottom - dy)),
    });
  }, []);
  const onTouchEnd = useCallback(() => {
    dragRef.current.dragging = false;
    if (!dragRef.current.moved) setOpen(o => !o);
  }, []);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      if (screenshot) fd.append("screenshot", screenshot);
      await api.post("/bugs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bug reported!");
      setTitle(""); setDescription(""); setScreenshot(null); setOpen(false);
    } catch (e) { toast.error("Failed to submit bug"); }
    setSubmitting(false);
  };

  if (!localStorage.getItem("dreamteam_user")) return null;
  if (isMobile) return null; // Hide on mobile — clean IG interface

  return (
    <>
      <button onMouseDown={onMouseDown} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{
        position:"fixed",bottom:`${pos.bottom}px`,right:`${pos.right}px`,zIndex:9999,
        padding: isMobile ? "6px 10px" : "10px 18px",
        height: isMobile ? "32px" : "42px",
        borderRadius:"24px",background:"rgba(239,68,68,0.85)",color:"white",border:"none",
        cursor:"grab",fontSize: isMobile ? "11px" : "13px",fontWeight:600,fontFamily:"var(--font-mono)",
        boxShadow:"0 4px 12px rgba(0,0,0,0.3)",letterSpacing:".05em",
        display:"flex",alignItems:"center",justifyContent:"center",gap: isMobile ? "4px" : "6px",transition:"none",userSelect:"none",
        opacity: isMobile ? 0.8 : 1,
      }} title="Report a Bug (drag to move)">{isMobile ? "🐛" : "🐛 REPORT BUG"}</button>

      {open && (
        <div style={{position:"fixed",bottom: isMobile ? "0" : `${pos.bottom + 62}px`,right: isMobile ? "0" : `${pos.right}px`, left: isMobile ? "0" : "auto", zIndex:9999,width: isMobile ? "100%" : "340px", borderRadius: isMobile ? "16px 16px 0 0" : "12px",
          background:"var(--s2)",border:"1px solid var(--bdr)",borderRadius:"12px",
          padding:"16px",boxShadow:"0 8px 32px rgba(0,0,0,0.4)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
            <h3 style={{fontFamily:"var(--font-orb)",fontSize:"14px",color:"var(--t1)",margin:0}}>Report a Bug</h3>
            <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:"var(--t3)",cursor:"pointer",fontSize:"18px",padding:"2px 6px",borderRadius:"4px",lineHeight:1}} title="Close">✕</button>
          </div>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Bug title *"
            style={{width:"100%",padding:"8px 12px",marginBottom:"8px",background:"var(--s1)",
            border:"1px solid var(--bdr)",borderRadius:"8px",color:"var(--t1)",fontSize:"13px",
            fontFamily:"var(--font-mono)",boxSizing:"border-box"}} />
          <textarea value={description} onChange={e=>setDescription(e.target.value)}
            placeholder="Describe the bug..." rows={3}
            style={{width:"100%",padding:"8px 12px",marginBottom:"8px",background:"var(--s1)",
            border:"1px solid var(--bdr)",borderRadius:"8px",color:"var(--t1)",fontSize:"13px",
            fontFamily:"var(--font-mono)",resize:"vertical",boxSizing:"border-box"}} />
          <div style={{marginBottom:"10px"}}
            onDragOver={e=>{e.preventDefault();e.stopPropagation();e.currentTarget.style.borderColor="rgba(6,182,212,0.8)";e.currentTarget.style.background="rgba(6,182,212,0.08)";}}
            onDragLeave={e=>{e.preventDefault();e.stopPropagation();e.currentTarget.style.borderColor="rgba(6,182,212,0.3)";e.currentTarget.style.background="transparent";}}
            onDrop={e=>{e.preventDefault();e.stopPropagation();e.currentTarget.style.borderColor="rgba(6,182,212,0.3)";e.currentTarget.style.background="transparent";const f=e.dataTransfer.files[0];if(f&&f.type.startsWith("image/"))setScreenshot(f);}}>
            <div onClick={()=>fileRef.current?.click()} style={{fontSize:"12px",padding:"10px 14px",
              background:"transparent",color:"var(--c)",border:"1px dashed rgba(6,182,212,0.3)",
              borderRadius:"8px",cursor:"pointer",textAlign:"center",transition:"all 0.2s"}}>
              {screenshot ? `📎 ${screenshot.name}` : "📸 Click or drag screenshot here"}
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}}
              onChange={e=>setScreenshot(e.target.files[0]||null)} />
          </div>
          <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
            <button onClick={()=>setOpen(false)} style={{padding:"6px 14px",background:"transparent",
              color:"var(--t2)",border:"1px solid var(--bdr)",borderRadius:"8px",cursor:"pointer",fontSize:"12px"}}>Cancel</button>
            <button onClick={submit} disabled={submitting} style={{padding:"6px 14px",
              background:"rgba(239,68,68,0.8)",color:"white",border:"none",borderRadius:"8px",
              cursor:"pointer",fontSize:"12px",fontWeight:"600"}}>{submitting?"Submitting...":"Submit Bug"}</button>
          </div>
        </div>
      )}
    </>
  );
}
